import React, { createContext, useContext, useState, useEffect } from 'react';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    // Check for existing session
    try {
      const savedUser = localStorage.getItem('bq_user');
      if (savedUser) {
        const parsed = JSON.parse(savedUser);
        if (parsed && typeof parsed === 'object' && parsed.progress) {
          setUser(parsed);
        } else {
          localStorage.removeItem('bq_user');
        }
      }
    } catch (e) {
      localStorage.removeItem('bq_user');
    }
    setIsLoading(false);
  }, []);

  const register = (phone, password, name) => {
    const users = JSON.parse(localStorage.getItem('bq_users') || '[]');
    
    // Check if user already exists
    const existingUser = users.find(u => u.phone === phone);
    if (existingUser) {
      throw new Error('Diese Telefonnummer ist bereits registriert.');
    }
    
    // Create new user
    const newUser = {
      id: Date.now().toString(),
      phone,
      password, // In production, this should be hashed
      name,
      createdAt: new Date().toISOString(),
      stats: {
        totalCards: 0,
        correctCards: 0,
        streak: 0,
        lastStudyDate: null
      },
      progress: {
        ntg: { studied: [], correct: [], incorrect: [] },
        bwh: { studied: [], correct: [], incorrect: [] },
        rwh: { studied: [], correct: [], incorrect: [] },
        mikp: { studied: [], correct: [], incorrect: [] },
        zib: { studied: [], correct: [], incorrect: [] }
      }
    };
    
    users.push(newUser);
    localStorage.setItem('bq_users', JSON.stringify(users));
    
    // Auto-login after registration
    const { password: _, ...userWithoutPassword } = newUser;
    setUser(userWithoutPassword);
    localStorage.setItem('bq_user', JSON.stringify(userWithoutPassword));
    
    return userWithoutPassword;
  };

  const login = (phone, password) => {
    const users = JSON.parse(localStorage.getItem('bq_users') || '[]');
    const foundUser = users.find(u => u.phone === phone && u.password === password);
    
    if (!foundUser) {
      throw new Error('Telefonnummer oder Passwort ist falsch.');
    }
    
    const { password: _, ...userWithoutPassword } = foundUser;
    setUser(userWithoutPassword);
    localStorage.setItem('bq_user', JSON.stringify(userWithoutPassword));
    
    return userWithoutPassword;
  };

  const logout = () => {
    setUser(null);
    localStorage.removeItem('bq_user');
  };

  const updateUserStats = (subjectId, cardId, isCorrect) => {
    if (!user) return;
    
    const users = JSON.parse(localStorage.getItem('bq_users') || '[]');
    const userIndex = users.findIndex(u => u.id === user.id);
    
    if (userIndex === -1) return;
    
    const updatedUser = { ...users[userIndex] };
    
    // Update progress
    if (!updatedUser.progress[subjectId].studied.includes(cardId)) {
      updatedUser.progress[subjectId].studied.push(cardId);
    }
    
    if (isCorrect) {
      if (!updatedUser.progress[subjectId].correct.includes(cardId)) {
        updatedUser.progress[subjectId].correct.push(cardId);
      }
      updatedUser.progress[subjectId].incorrect = 
        updatedUser.progress[subjectId].incorrect.filter(id => id !== cardId);
    } else {
      if (!updatedUser.progress[subjectId].incorrect.includes(cardId)) {
        updatedUser.progress[subjectId].incorrect.push(cardId);
      }
      updatedUser.progress[subjectId].correct = 
        updatedUser.progress[subjectId].correct.filter(id => id !== cardId);
    }
    
    // Update stats
    updatedUser.stats.totalCards = Object.values(updatedUser.progress)
      .reduce((sum, p) => sum + p.studied.length, 0);
    updatedUser.stats.correctCards = Object.values(updatedUser.progress)
      .reduce((sum, p) => sum + p.correct.length, 0);
    
    // Update streak
    const today = new Date().toDateString();
    const lastStudy = updatedUser.stats.lastStudyDate;
    
    if (lastStudy !== today) {
      const yesterday = new Date();
      yesterday.setDate(yesterday.getDate() - 1);
      
      if (lastStudy === yesterday.toDateString()) {
        updatedUser.stats.streak += 1;
      } else if (lastStudy !== today) {
        updatedUser.stats.streak = 1;
      }
      updatedUser.stats.lastStudyDate = today;
    }
    
    users[userIndex] = updatedUser;
    localStorage.setItem('bq_users', JSON.stringify(users));
    
    const { password: _, ...userWithoutPassword } = updatedUser;
    setUser(userWithoutPassword);
    localStorage.setItem('bq_user', JSON.stringify(userWithoutPassword));
  };

  const getSubjectProgress = (subjectId) => {
    if (!user || !user.progress[subjectId]) {
      return { studied: 0, correct: 0, incorrect: 0, total: 50 };
    }
    
    const progress = user.progress[subjectId];
    return {
      studied: progress.studied.length,
      correct: progress.correct.length,
      incorrect: progress.incorrect.length,
      total: 50
    };
  };

  const value = {
    user,
    isLoading,
    isAuthenticated: !!user,
    register,
    login,
    logout,
    updateUserStats,
    getSubjectProgress
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;